import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Box, Stack } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import CustomAutoComplete from "components/Dropdown/CustomAutoComeplete";

// Icons and Constants
import Constants, { Colors } from "utils/Constants";

// Models
import { TAX_TYPES, createTaxRegistration } from "./models/TaxRegistration";

function TaxRegistrationField({
  initialValues = [],
  onChange,
  error,
  helperText,
  onRegistrationChange,
}) {
  const [registrations, setRegistrations] = useState(initialValues);
  const [selectedType, setSelectedType] = useState("");

  // Sync internal state with initialValues prop when it changes
  useEffect(() => {
    setRegistrations(initialValues);
  }, [initialValues]);

  const usedTypes = registrations.map((r) => r.type);
  const availableTypes = TAX_TYPES.filter((t) => !usedTypes.includes(t));

  const handleAddRegistration = () => {
    if (!selectedType) {
      return;
    }

    const updated = [...registrations, createTaxRegistration(selectedType)];
    setRegistrations(updated);
    onChange?.(updated);
    onRegistrationChange?.(updated);
    setSelectedType("");
  };

  return (
    <MDBox>
      <Box>
        <MDTypography
          variant="caption"
          sx={{
            fontSize: "14px",
            fontWeight: 500,
            color: "#344054",
            marginBottom: "4px",
          }}
        >
          Select Tax Type (BTW/VAT/Other)
        </MDTypography>
        <Stack
          direction="row"
          spacing={1}
          alignItems="flex-start"
          sx={{
            display: "flex",
            flexWrap: "nowrap",
            width: "100%",
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <CustomAutoComplete
              label=""
              name="taxType"
              id="taxType"
              hint="Select"
              getOptionLabel={(option) => {
                if (typeof option === "string") {
                  const found = availableTypes.find((t) => t === option);
                  return found || "";
                }
                return option?.title || "";
              }}
              menu={availableTypes.map((type) => ({
                [Constants.MONGOOSE_ID]: type,
                title: type,
              }))}
              value={selectedType}
              handleChange={(e) => setSelectedType(e.target.value || "")}
              valueStyle={{
                backgroundColor: Colors.WHITE,
                height: "50px",
                verticalMarginTop: "4px",
              }}
              error={Boolean(error || helperText)}
              helperText={error || helperText}
            />
          </Box>
          <Box sx={{ flexShrink: 0 }}>
            <MDButton
              variant="contained"
              color="info"
              sx={{
                boxShadow: "none",
                textTransform: "none",
                height: "50px",
                whiteSpace: "nowrap",
              }}
              disabled={availableTypes.length === 0 || !selectedType}
              onClick={handleAddRegistration}
            >
              Add
            </MDButton>
          </Box>
        </Stack>
      </Box>
    </MDBox>
  );
}

TaxRegistrationField.propTypes = {
  initialValues: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      value: PropTypes.string,
      documents: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          url: PropTypes.string,
          size: PropTypes.number,
          status: PropTypes.string,
        })
      ),
    })
  ),
  onChange: PropTypes.func,
  error: PropTypes.string,
  helperText: PropTypes.string,
  onRegistrationChange: PropTypes.func,
};

TaxRegistrationField.defaultProps = {
  initialValues: [],
  onChange: () => {},
  error: null,
  helperText: null,
  onRegistrationChange: null,
};

export default TaxRegistrationField;
